import { useState, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { DashboardLayout } from '../components/layout/DashboardLayout';
import { Property } from '../types/user';
import { PropertyCard } from '../components/property/PropertyCard';
import { PropertyFormModal } from '../components/property/PropertyFormModal';
import { Button } from '../components/ui/Button';
import { PlusIcon, MagnifyingGlassIcon } from '@heroicons/react/24/outline';
import { useProperties } from '../hooks/useProperties';

export const AdminDashboard = () => {
  const navigate = useNavigate();
  const { properties, loading, createProperty, updateProperty, deleteProperty } = useProperties();
  const [showPropertyModal, setShowPropertyModal] = useState(false);
  const [editingProperty, setEditingProperty] = useState<Property | null>(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [sortOrder, setSortOrder] = useState<'asc' | 'desc'>('asc');
  const [propertyForm, setPropertyForm] = useState({
    name: '',
    location: ''
  });

  // Filtrar y ordenar propiedades
  const filteredAndSortedProperties = useMemo(() => {
    let filtered = properties;

    // Filtrar por búsqueda
    if (searchTerm) {
      filtered = properties.filter(property =>
        property.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
        property.location.toLowerCase().includes(searchTerm.toLowerCase())
      );
    }

    // Ordenar alfabéticamente
    return [...filtered].sort((a, b) => {
      const comparison = a.name.localeCompare(b.name, 'es');
      return sortOrder === 'asc' ? comparison : -comparison;
    });
  }, [properties, searchTerm, sortOrder]);

  const handleUserManagement = (propertyId: string) => {
    navigate(`/property/${propertyId}?tab=users`);
  };

  const resetForm = () => {
    setPropertyForm({ name: '', location: '' });
    setEditingProperty(null);
  };

  const handleSubmit = async (data: { name: string; location: string }) => {
    try {
      if (editingProperty) {
        await updateProperty({
          ...editingProperty,
          name: data.name,
          location: data.location
        });
      } else {
        await createProperty(data);
      }
      setShowPropertyModal(false);
      resetForm();
    } catch (error) {
      console.error('Error:', error);
    }
  };

  if (loading) {
    return (
      <DashboardLayout>
        <div className="flex items-center justify-center h-full">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-red-500" />
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout>
      <div className="space-y-6">
        <div className="flex justify-between items-center">
          <h1 className="text-2xl font-bold">Gestión de Propiedades</h1>
          <Button
            onClick={() => {
              resetForm();
              setShowPropertyModal(true);
            }}
          >
            <PlusIcon className="h-5 w-5 mr-2" />
            Nueva Propiedad
          </Button>
        </div>

        {/* Barra de búsqueda y ordenamiento */}
        <div className="flex flex-col sm:flex-row gap-4 items-start sm:items-center">
          <div className="relative flex-1 w-full">
            <MagnifyingGlassIcon className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400" />
            <input
              type="text"
              placeholder="Buscar por nombre o ubicación..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-transparent"
            />
          </div>
          <button
            onClick={() => setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc')}
            className="flex items-center space-x-2 px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors whitespace-nowrap"
          >
            <span className="text-sm font-medium">
              {sortOrder === 'asc' ? '↑ A-Z' : '↓ Z-A'}
            </span>
          </button>
        </div>

        {/* Contador de resultados */}
        {searchTerm && (
          <p className="text-sm text-gray-600">
            {filteredAndSortedProperties.length} de {properties.length} propiedades
          </p>
        )}

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {filteredAndSortedProperties.map((property) => (
            <PropertyCard
              key={property.id}
              property={property}
              onEdit={() => {
                setEditingProperty(property);
                setPropertyForm({
                  name: property.name,
                  location: property.location
                });
                setShowPropertyModal(true);
              }}
              onDelete={() => deleteProperty(property.id)}
              onUserManagement={() => handleUserManagement(property.id)}
            />
          ))}
        </div>

        <PropertyFormModal
          isOpen={showPropertyModal}
          onClose={() => setShowPropertyModal(false)}
          onSubmit={handleSubmit}
          editingProperty={editingProperty}
          initialData={propertyForm}
        />
      </div>
    </DashboardLayout>
  );
};